/**
 * Diğer › Geçmiş cevaplar (v0.1.38): the shop's own past replies, newest first, each with the
 * customer's question as far as it was recovered, the product the words matched and the
 * language. The Temsilci takes the most similar ones as examples for a new draft; Sercan
 * retires a wrong or stale one here and brings it back if needed. Nothing is applied
 * optimistically: the list is read again after the server answers.
 */

import { useEffect, useState } from 'react';
import {
  LANGUAGE_LABELS,
  matchesQuery,
  REPLY_EXAMPLE_SOURCE_LABELS,
  REPLY_EXAMPLE_STATUS_LABELS,
  type ReplyExample,
} from '../api/reply-examples.js';
import { useStore } from '../app/context.js';
import { type ReplyExamplesState, useReplyExamples } from '../app/reply-examples.js';
import { formatLongDate } from '../lib/format.js';
import { EmptyState, Segmented } from '../screens/common.js';
import { Icon } from './icons.js';

type ReplyFilter = 'etkin' | 'emekli' | 'tumu';
const PAGE = 50;

function StatusLine({ state, reload }: { state: ReplyExamplesState; reload: () => void }) {
  if (state.error !== null && state.view === null) {
    return (
      <div className="shop-status" role="status">
        Geçmiş cevaplar yüklenemedi: {state.error}{' '}
        <button type="button" className="btn btn--secondary" onClick={reload}>
          Tekrar dene
        </button>
      </div>
    );
  }
  return (
    <div className="shop-status" role="status">
      Yükleniyor…
    </div>
  );
}

function ReplyCard({ example, busy }: { example: ReplyExample; busy: boolean }) {
  const store = useStore();
  const { store: examples } = useReplyExamples();
  const retire = async () => {
    const error = await examples.retire(example.id);
    if (error === null) store.showToast('Emekliye ayrıldı; Temsilci artık örnek almaz', { icon: 'info' });
    else store.showToast(`Ayrılamadı: ${error}`, { icon: 'alert' });
  };
  const restore = async () => {
    const error = await examples.restore(example.id);
    if (error === null) store.showToast('Geri getirildi', { icon: 'check' });
    else store.showToast(`Getirilemedi: ${error}`, { icon: 'alert' });
  };
  const statusClass = example.status === 'active' ? 'approved' : 'retired';
  const language = example.language === null ? null : (LANGUAGE_LABELS[example.language] ?? example.language);
  return (
    <li className={`kb-card kb-card--${statusClass}`} aria-label={example.subject ?? 'Konu yok'}>
      <header className="kb-card__head">
        <div className="kb-card__title">
          <strong>{example.subject ?? '(konu yok)'}</strong>
          <span className="kb-card__meta">
            {formatLongDate(example.sentAt)}
            {example.productTitle ? ` · ${example.productTitle}` : ''}
            {example.productSku ? ' · ' : ''}
            {example.productSku ? <span className="mono">{example.productSku}</span> : null}
            {language ? ` · ${language}` : ''}
          </span>
        </div>
        <span className={`kb-status kb-status--${statusClass}`}>{REPLY_EXAMPLE_STATUS_LABELS[example.status]}</span>
      </header>
      {example.question !== null ? (
        <label className="kb-field">
          <span className="kb-field__label">
            {example.paired ? 'Müşterinin sorusu' : 'Müşterinin sorusu (alıntıdan)'}
          </span>
          <textarea className="kb-field__text" value={example.question} readOnly rows={4} />
        </label>
      ) : (
        <p className="kb-card__questions">Soru bulunamadı: gelen posta kutuda yok, alıntı da yok.</p>
      )}
      <label className="kb-field">
        <span className="kb-field__label">Senin cevabın</span>
        <textarea className="kb-field__text" value={example.answer} readOnly rows={6} />
      </label>
      <footer className="kb-card__foot">
        <span className="kb-card__time">
          {REPLY_EXAMPLE_SOURCE_LABELS[example.source]}
          {example.vehicleHint ? ` · araç: ${example.vehicleHint}` : ''}
        </span>
        <span className="kb-card__actions">
          {example.status === 'active' ? (
            <button type="button" className="btn btn--secondary" disabled={busy} onClick={() => void retire()}>
              Emekliye ayır
            </button>
          ) : (
            <button type="button" className="btn btn--secondary" disabled={busy} onClick={() => void restore()}>
              Geri getir
            </button>
          )}
        </span>
      </footer>
    </li>
  );
}

export function ReplyExamplesPage() {
  const { store, state } = useReplyExamples();
  const [filter, setFilter] = useState<ReplyFilter>('etkin');
  const [query, setQuery] = useState('');
  useEffect(() => {
    void store.ensureLoaded();
  }, [store]);
  if (state.view === null) return <StatusLine state={state} reload={() => void store.reload()} />;
  const view = state.view;
  if (view.examples.length === 0) {
    return (
      <EmptyState
        title="Henüz geçmiş cevap yok"
        text="Gmail bağlanınca Gönderilmiş klasörü her gece 04:30'da okunur; müşterilere verdiğin cevaplar burada birikir ve Temsilci'ye örnek olur."
      />
    );
  }
  const options: { id: ReplyFilter; label: string; count: number | null }[] = [
    { id: 'etkin', label: 'Etkin', count: view.counts.active },
    { id: 'emekli', label: 'Emekli', count: view.counts.retired },
    { id: 'tumu', label: 'Tümü', count: view.counts.total },
  ];
  const byStatus =
    filter === 'tumu'
      ? view.examples
      : view.examples.filter((example) => example.status === (filter === 'etkin' ? 'active' : 'retired'));
  const shown = byStatus.filter((example) => matchesQuery(example, query));
  const span =
    view.oldestSentAt !== null && view.newestSentAt !== null
      ? ` ${formatLongDate(view.oldestSentAt)} ile ${formatLongDate(view.newestSentAt)} arası.`
      : '';
  return (
    <section className="kb-page" aria-label="Geçmiş cevaplar">
      {/* The section page says what this is in one line; the longer help opens here (audit 25 Oct 2026, F18). */}
      <details className="kb-page__help">
        <summary className="kb-page__help-summary">
          <Icon name="chevron-down" size={16} />
          Nasıl çalışır
        </summary>
        <p className="kb-page__lead">
          Gönderilmiş klasöründen alınan cevapların: {view.counts.total} cevap, {view.counts.paired} tanesi gelen
          soruyla eşleşti, {view.counts.withProduct} tanesinde ürün bulundu.{span} Temsilci her yeni e-postada en benzer
          üçünü örnek alır; yanlış ya da eskimiş olanı emekliye ayır.
        </p>
      </details>
      <label className="kb-field">
        <span className="kb-field__label">Ara</span>
        <input
          className="kb-field__text"
          type="search"
          value={query}
          placeholder="konu, soru, cevap veya ürün"
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>
      <Segmented label="Durum" options={options} value={filter} onChange={setFilter} controls="reply-list" />
      {shown.length === 0 ? (
        <p className="kb-page__empty">Bu süzgeçte cevap yok.</p>
      ) : (
        <ul id="reply-list" className="kb-list" aria-label="Geçmiş cevaplar listesi">
          {shown.slice(0, PAGE).map((example) => (
            <ReplyCard
              key={`${example.id}-${example.status}`}
              example={example}
              busy={state.busy.includes(example.id)}
            />
          ))}
        </ul>
      )}
      {shown.length > PAGE ? (
        <p className="kb-page__empty">
          {shown.length} cevaptan ilk {PAGE} tanesi gösteriliyor; aramayla daralt.
        </p>
      ) : null}
    </section>
  );
}
